import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  ShieldCheck,
  Zap,
  Minimize2,
  Loader2,
  Download,
  Save,
  Eye,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { isDesktop } from '@/lib/fileSystem';
import { normalizeAiLevel } from '@/lib/aiAction';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import {
  compressPDFLow,
  formatFileSize,
} from '@/lib/pdfOperations';
import { compressPDF as nativeCompress } from '@/lib/nativebridge';
import { classifyCloudError } from '@/lib/cloudError';
import PageCanvas from '@/components/PageCanvas';
import { useAiPrefill } from '@/hooks/useAiPrefill';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

const LEVELS = [
  {
    id: 'low',
    label: 'Low',
    icon: ShieldCheck,
    description: 'Optimize PDF structure',
    detail: 'Prioritizes image quality',
  },
  {
    id: 'medium',
    label: 'Medium',
    icon: Zap,
    description: 'Balanced compression',
    // The browser fallback at this tier is lossless (pdf-lib re-save). Cloud-
    // authenticated users hit the multi-engine orchestrator, which may
    // re-encode images at higher quality and still preserve text. Either
    // way, text remains selectable.
    detail: 'Preserves text and images',
    recommended: true,
  },
  {
    id: 'high',
    label: 'High',
    icon: Minimize2,
    description: 'Maximum compression',
    detail: 'Smaller images; keeps text and form fields',
    warning: 'Image quality may decrease',
  },
];

/**
 * Compress — Reduce PDF file size.
 *
 * Three compression levels:
 *   Low: strip metadata, re-save
 *   Medium: re-render pages at 150 DPI as JPEG (0.75 quality)
 *   High: re-render pages at 72 DPI as JPEG (0.5 quality)
 */
export default function Compress() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile, saveFile: saveFileDialog } = useFileDialog();
  const location = useLocation();
  const { t } = useTranslation('viewer');

  // File state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Compression state
  const [level, setLevel] = useState('medium');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState(null);

  // Result state — alreadyOptimized: true when compression couldn't reduce size
  const [result, setResult] = useState(null); // { bytes, blob, size, alreadyOptimized }
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf') return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setResult(null);
      setFileName(file.name);
      setFileSize(file.size);

      try {
        // Decrypt owner-password PDFs (lossless) / reject truly locked ones
        // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
        const buf = await readPdfLibBytes(file);
        setArrayBuffer(buf);
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        setPdfDoc(doc);
        rememberDocument(file);
        setNumPages(doc.numPages);
      } catch (err) {
        setError(pdfIntakeErrorMessage(err, 'compress it'));
      }
    },
    [pdfDoc, rememberDocument]
  );

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadFile(file);
  }, [openFile, loadFile]);

  // AI prefill: load file from router state + apply aiLevel.
  // See useAiPrefill for the ref-guard / no-strip-navigate rationale.
  const { aiPrefilled } = useAiPrefill({
    location,
    loadFn: loadFile,
    onPrefill: (state) => {
      const aiLevel = normalizeAiLevel(state.aiLevel);
      if (aiLevel) setLevel(aiLevel);
    },
  });

  // ==================== Compress ====================

  const handleCompress = useCallback(async () => {
    if (!arrayBuffer || !pdfDoc) return;

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    try {
      let compressedBytes;

      // Try server-side multi-engine pipeline (desktop native or cloud API).
      // For web requests, the cloud orchestrator returns X-Scrivar-Compression-*
      // headers describing the winning strategy + classification — capture
      // them via the onMeta callback so the success branch can display them.
      //
      // Distinguish auth errors from network/server errors so the UI can show
      // a "sign in for better compression" hint rather than silently degrading.
      setProgressLabel('Compressing…');
      let serverMeta = null;
      let cloudFailureReason = null;  // 'rate_limited' | 'auth' | 'network' | 'server' | 'unreachable' | null
      try {
        const nativeResult = await nativeCompress(arrayBuffer, level, {
          onMeta: (headers) => {
            if (!headers || typeof headers.get !== 'function') return;
            serverMeta = {
              strategy: headers.get('x-scrivar-compression-strategy') || null,
              reduction: headers.get('x-scrivar-compression-reduction') || null,
              classification: headers.get('x-scrivar-compression-classification') || null,
              verified: headers.get('x-scrivar-compression-verified') === 'true',
              engine: headers.get('x-scrivar-compression-engine') || null,
              fellBack: headers.get('x-scrivar-compression-fellback') === 'true',
            };
          },
        });
        if (nativeResult) {
          compressedBytes = new Uint8Array(nativeResult);
          const alreadyOptimized = compressedBytes.length >= fileSize;
          const blob = new Blob([compressedBytes], { type: 'application/pdf' });
          setResult({
            bytes: compressedBytes,
            blob,
            size: compressedBytes.length,
            alreadyOptimized,
            serverMeta,
          });
          setProgress(1);
          setProgressLabel('');
          setProcessing(false);
          return;
        }
        // nativeResult is null: no error was thrown, but the cloud/native
        // engine was unreachable (e.g. web with no cloud connection). This is
        // NOT the same as never having tried — flag it so the fallback result
        // never gets presented as full-engine work.
        cloudFailureReason = 'unreachable';
      } catch (err) {
        // Classify (incl. 429 → 'rate_limited') so we can show the right
        // message instead of silently running the weak browser fallback.
        if (isDesktop()) throw err;
        cloudFailureReason = classifyCloudError(err);
      }

      // Anonymous user hit the per-IP daily cap on the server. Show the sign-up
      // nudge — do NOT degrade to the pdf-lib fallback (that would mask the cap
      // with a 0% "result").
      if (cloudFailureReason === 'rate_limited') {
        setShowFreeTierModal(true);
        setProgress(0);
        setProgressLabel('');
        setProcessing(false);
        return;
      }

      // The browser fallback repacks PDF objects without rasterizing pages.
      // Selecting stronger compression never silently removes text/forms.
      setProgressLabel('Optimizing PDF structure…');
      compressedBytes = await compressPDFLow(arrayBuffer, setProgress);

      const alreadyOptimized = compressedBytes.length >= fileSize;
      const blob = new Blob([compressedBytes], { type: 'application/pdf' });
      setResult({
        bytes: compressedBytes,
        blob,
        size: compressedBytes.length,
        alreadyOptimized,
        // Surface why we ended up on the browser fallback. The UI uses this
        // to hint at signing in for the multi-engine cloud pipeline.
        cloudFailureReason,
      });
      setProgress(1);
      setProgressLabel('');
    } catch (err) {
      setError(`Compression failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, pdfDoc, level, numPages]);

  // ==================== Result actions ====================

  const openInViewer = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.blob], `${baseName}_compressed.pdf`, {
      type: 'application/pdf',
    });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate]);

  const handleDownload = useCallback(async () => {
    if (!result) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    const baseName = fileName.replace(/\.pdf$/i, '');
    await saveFileDialog(result.bytes, `${baseName}_compressed.pdf`);
    if (usageCapApplies()) incrementUsage();
  }, [result, fileName, saveFileDialog]);

  // ==================== Derived ====================

  const reduction = result ? Math.round((1 - result.size / fileSize) * 100) : 0;
  const sizeBarWidth = result ? Math.max(5, Math.round((result.size / fileSize) * 100)) : 100;

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Compress PDF</h1>
          <p className="text-sm text-gray-500">Reduce file size</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        {/* Drop zone */}
        {!pdfDoc && (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging
                  ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
                  : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Select a file to compress</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        )}


        {pdfDoc && (
          <>
            {/* File info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center gap-3">
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}
                </p>
              </div>
              <button
                onClick={handleBrowse}
                className="text-sm text-brand-blue hover:underline font-medium"
              >
                Change file
              </button>
            </div>

            {/* Compression levels */}
            {!result && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {LEVELS.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setLevel(l.id)}
                      disabled={processing}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        level === l.id
                          ? 'border-brand-blue bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {l.recommended && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-medium bg-brand-blue text-white px-2 py-0.5 rounded-full">
                          Recommended
                        </span>
                      )}
                      <l.icon
                        size={22}
                        className={level === l.id ? 'text-brand-blue mb-2' : 'text-gray-400 mb-2'}
                      />
                      <p className="font-semibold text-gray-900 text-sm">{l.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{l.description}</p>
                      {l.warning && (
                        <p className="flex items-center gap-1 text-[10px] text-amber-600 mt-1.5">
                          <AlertTriangle size={10} />
                          {l.warning}
                        </p>
                      )}
                    </button>
                  ))}
                </div>

                {/* AI prefill banner */}
                {aiPrefilled && !processing && !result && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{t('ai.banner_compress')}</span>
                  </div>
                )}

                {/* Compress button */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                  {processing && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={16} className="animate-spin text-brand-blue" />
                        <span className="text-sm text-gray-600">
                          {progressLabel || `${Math.round(progress * 100)}%`}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-blue rounded-full transition-all duration-200"
                          style={{ width: `${progress * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleCompress}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minimize2 size={18} />
                    Compress PDF
                  </button>
                </div>
              </>
            )}

            {/* Results — already optimized */}
            {result && result.alreadyOptimized && (
              <div className="bg-white rounded-xl border border-green-200 p-6 text-center">
                <CheckCircle size={40} className="mx-auto mb-3 text-green-500" />
                <p className="text-lg font-semibold text-gray-900 mb-1">
                  This PDF is already as small as we can make it
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  {result.serverMeta
                    ? 'We tried multiple compression strategies and none beat the original. Your file is already at '
                    : `No further compression is possible at the ${level} level — your file is already at `}
                  {formatFileSize(fileSize)}.
                </p>
                {/* Surface that we ran the local fallback because cloud was
                    unavailable. Anonymous + auth-failed users see this hint;
                    signed-in cloud calls leave cloudFailureReason null. */}
                {result.cloudFailureReason === 'auth' && (
                  <p className="text-xs text-gray-500 mb-4">
                    Sign in to access our multi-engine compression pipeline for stronger results.
                  </p>
                )}
                {result.cloudFailureReason === 'network' && (
                  <p className="text-xs text-gray-500 mb-4">
                    We couldn't reach the compression server. Try again in a moment.
                  </p>
                )}
                {result.cloudFailureReason === 'unreachable' && (
                  <p className="text-xs text-gray-500 mb-4">
                    The compression engine couldn't be reached — this result used the basic
                    browser optimizer. Try again for stronger compression.
                  </p>
                )}
                {result.serverMeta?.fellBack && (
                  <p className="text-xs text-amber-600 mb-4">
                    Our premium compression engine is temporarily unavailable, and our standard engine couldn't improve on this file.
                  </p>
                )}
                <button
                  onClick={() => setResult(null)}
                  className="px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                >
                  Try a different level
                </button>
              </div>
            )}

            {/* Results — compression successful */}
            {result && !result.alreadyOptimized && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {/* Size comparison */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-3 text-2xl font-bold text-gray-900 mb-1">
                    <span>{formatFileSize(fileSize)}</span>
                    <ArrowRight size={20} className="text-gray-400" />
                    <span className="text-green-600">{formatFileSize(result.size)}</span>
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    {reduction}% smaller
                  </p>
                  {/* Surface the orchestrator's chosen strategy when present.
                      Only renders on cloud responses; desktop IPC currently
                      returns bytes-only and serverMeta stays null. */}
                  {result.serverMeta?.strategy && result.serverMeta.strategy !== 'identity' && (
                    <p className="mt-2 text-xs text-gray-400">
                      Compressed using <span className="font-mono">{result.serverMeta.strategy}</span>
                    </p>
                  )}
                  {result.serverMeta?.fellBack && (
                    <p className="mt-2 text-xs text-amber-600">
                      Our premium compression engine is temporarily unavailable — used our standard engine instead.
                    </p>
                  )}
                  {/* Browser-fallback hint: encourages signing in for the
                      multi-engine pipeline. Suppressed when serverMeta is
                      present (= cloud actually ran). */}
                  {!result.serverMeta && result.cloudFailureReason === 'auth' && (
                    <p className="mt-2 text-xs text-gray-500">
                      Sign in for stronger compression on this file.
                    </p>
                  )}
                  {/* The real engine never ran — a 1-2% pdf-lib shave should
                      never look like the full-engine result. */}
                  {!result.serverMeta &&
                    ['unreachable', 'network', 'server'].includes(result.cloudFailureReason) && (
                    <p className="mt-2 text-xs text-gray-500">
                      The compression engine couldn't be reached — this result used the basic
                      browser optimizer. Try again for stronger compression.
                    </p>
                  )}
                </div>

                {/* Visual size bar */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
                    <span>Original</span>
                    <div className="flex-1" />
                    <span>Compressed</span>
                  </div>
                  <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all duration-500"
                      style={{ width: `${sizeBarWidth}%` }}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={openInViewer}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                  >
                    <Eye size={16} />
                    Open in Viewer
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    {isDesktop()
                      ? (<><Save size={16} />Save</>)
                      : (<><Download size={16} />Download</>)}
                  </button>
                </div>

                <button
                  onClick={() => setResult(null)}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
                >
                  Try a different compression level
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}
